import { Spin, Typography } from 'antd'
import React, { FC, ReactElement, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { useTranslation } from 'react-i18next'
import { ExpandCollapseChevronButton } from '../../../../../Components/UI'
import { ProposalCard } from '../ProposalCard/ProposalCard'
import {
  ProposalsEventFragment,
  ProposalsGetEmissionsForLocationsResponseFragment,
  ProposalsProposalFragment,
  useProposalsGetEmissionsForLocationsQuery,
} from './Proposals.generated-types'

const { Text } = Typography

interface ProposalsProps {
  event: ProposalsEventFragment
  defaultExpanded?: boolean
  refetchEvent: () => void
}

export const Proposals: FC<ProposalsProps> = ({
  event,
  event: { proposals },
  defaultExpanded,
  refetchEvent,
}): ReactElement => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)

  const proposalUUIDs: string[] = proposals.map((proposal) => proposal.UUID)

  const [{ data: getEmissionsForLocationsQueryData, fetching }] =
    useProposalsGetEmissionsForLocationsQuery({
      variables: { input: { locationIds: proposalUUIDs } },
    })

  if (fetching) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px',
        }}
      >
        <Spin />
      </div>
    )
  }

  const proposalUUIDsToTotalEmissionsForJourneys =
    getEmissionsForLocationsQueryData?.getEmissionsForLocations.length
      ? new Map(
        getEmissionsForLocationsQueryData.getEmissionsForLocations
          .filter(
            (emissionsForLocation) =>
              emissionsForLocation.emissionsForJourneys.length
          )
          .map((emissionsForLocation) => [
            emissionsForLocation.locationId,
            calculateTotalEmissionsForAllJourneys(emissionsForLocation),
          ])
      )
      : null

  const proposalUUIDKeysWithTotalEmissionsForJourneysPairs =
    proposalUUIDsToTotalEmissionsForJourneys
      ? Array.from(proposalUUIDsToTotalEmissionsForJourneys.entries())
      : []

  /*
   * Create an array of key value pairs, with each key the UUID of the proposal and the value being the emissions. Compare emissions of the
   * previous key/value pair and if the current key/value pair's emissions are lower, keep a record, otherwise use the previous value.
   * When finished iterating, get the key which is the UUID of the proposal and the 0th element. The 0th element is always the key in a key/value
   * pair produced by .entries().
   */
  const proposalUUIDWithLowestTotalEmissions: string | null =
    proposalUUIDKeysWithTotalEmissionsForJourneysPairs.length
      ? proposalUUIDKeysWithTotalEmissionsForJourneysPairs.reduce(
        (previousValue, currentValue) =>
          Math.min(previousValue[1], currentValue[1]) === previousValue[1]
            ? previousValue
            : currentValue
      )[0]
      : null

  const handleExpandCollapse = (): void => setExpanded(!expanded)

  return (
    <>
      <div
        style={{
          marginTop: '40px',
        }}
      >
        <AnimateHeight
          duration={Math.min(proposals.length * 250, 1000)}
          height={expanded ? 'auto' : 90 + 8 * proposals.length}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {getDisplayOrderOfEventProposals(
              event,
              proposalUUIDWithLowestTotalEmissions
            ).map((proposal, index) => (
              <ProposalCard
                event={event}
                refetchEvent={refetchEvent}
                proposal={proposal}
                expanded={expanded}
                handleExpandCollapse={handleExpandCollapse}
                responseIndex={index}
                key={proposal.UUID}
                totalEmissionsForAllJourneys={
                  proposalUUIDsToTotalEmissionsForJourneys
                    ? proposalUUIDsToTotalEmissionsForJourneys.get(
                      proposal.UUID
                    ) ?? null
                    : null
                }
                displayLowestTravelEmissionsBanner={
                  proposalUUIDWithLowestTotalEmissions === proposal.UUID
                }
              />
            ))}
          </div>
        </AnimateHeight>
      </div>
      {!defaultExpanded && expanded && (
        <div
          style={{
            marginBottom: '25px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <ExpandCollapseChevronButton
            expanded={expanded}
            handleExpandCollapse={handleExpandCollapse}
          />
          <Text strong>
            {t(
              'eventHub.inProgress.responses.hideOthers',
              'Hide other responses'
            )}
          </Text>
        </div>
      )}
    </>
  )
}

const getDisplayOrderOfEventProposals = (
  event: ProposalsEventFragment,
  proposalUUIDWithLowestTotalEmissions: string | null
): ProposalsProposalFragment[] => {
  return event.proposals
    .filter((p) => p.status === 'SentToGuest')
    .sort(
      (
        proposalA: ProposalsProposalFragment,
        proposalB: ProposalsProposalFragment
      ) => {
        if (proposalA.UUID === proposalUUIDWithLowestTotalEmissions) {
          return -1
        }

        if (proposalB.UUID === proposalUUIDWithLowestTotalEmissions) {
          return 1
        }

        return (
          Number(proposalB.hasPreferredVenue) -
          Number(proposalA.hasPreferredVenue)
        )
      }
    )
}

const calculateTotalEmissionsForAllJourneys = (
  emissionsForLocations: ProposalsGetEmissionsForLocationsResponseFragment
): number => {
  const totalEmissionsForAllJourneys: number =
    emissionsForLocations.emissionsForJourneys.reduce(
      (currentSum, emissionsForJourney) =>
        currentSum + emissionsForJourney.calculatedEmissions,
      0
    )

  return totalEmissionsForAllJourneys
}
